import { createContext } from 'react';

/**
 * Creates a React context that preserves its identity across Vite HMR updates.
 *
 * Without this, an HMR update re-executes the module and calls createContext()
 * again, producing a new context object. Mounted Providers still reference the
 * old context while consumers reference the new one → "must be used within
 * Provider" errors even though the Provider is there.
 *
 * The fix: stash the context in `import.meta.hot.data` so subsequent HMR
 * evaluations reuse the same object. In production builds `import.meta.hot` is
 * undefined, so the function falls back to a plain createContext().
 */
export function createHMRProofContext<T>(
  name: string,
  defaultValue: T,
): React.Context<T> {
  if (import.meta.hot && import.meta.hot.data) {
    const existing = import.meta.hot.data[name] as React.Context<T> | undefined;
    if (existing) return existing;

    const ctx = createContext<T>(defaultValue);
    import.meta.hot.data[name] = ctx;
    return ctx;
  }

  return createContext<T>(defaultValue);
}
